import type { ParsedFile } from '@/features/parties/import/lib/types';
import { resolveEquipmentId, type EquipmentOption } from './equipment';
import {
  hasEmail,
  hasEquipment,
  hasMobile,
  type ImportResourceType,
  type ResourceColumnMapping,
  type ResourceField,
  type ResourceImportDefaults,
  type ResourceImportDraft,
} from './types';

/** Mapped cell value for a target field, trimmed. '' when unmapped or blank. */
function val(
  row: Record<string, string>,
  mapping: ResourceColumnMapping,
  field: ResourceField,
): string {
  const header = mapping[field];
  if (!header) return '';
  return (row[header] ?? '').trim();
}

/**
 * Materialise the parsed rows into editable drafts for the chosen resource
 * type. Fields the type doesn't allow are left empty (the BE validator rejects
 * them otherwise), and the equipment-type text is resolved to an org
 * equipment-type id up front so the preview can flag what didn't match.
 */
export function buildResourceDrafts(args: {
  parsed: ParsedFile;
  mapping: ResourceColumnMapping;
  type: ImportResourceType;
  defaults: ResourceImportDefaults;
  equipmentIndex: Map<string, string>;
  equipmentOptions: EquipmentOption[];
}): ResourceImportDraft[] {
  const { parsed, mapping, type, defaults, equipmentIndex, equipmentOptions } = args;

  return parsed.rows.map((row, i) => {
    const equipmentTypeText = hasEquipment(type) ? val(row, mapping, 'equipmentType') : '';
    return {
      rowRef: `r${i}`,
      type,
      name: val(row, mapping, 'name'),
      displayName: val(row, mapping, 'displayName'),
      reference: val(row, mapping, 'reference'),
      mobileNumber: hasMobile(type) ? val(row, mapping, 'mobileNumber') : '',
      email: hasEmail(type) ? val(row, mapping, 'email') : '',
      equipmentTypeText,
      organisationEquipmentTypeId: equipmentTypeText
        ? resolveEquipmentId(equipmentTypeText, equipmentIndex, equipmentOptions)
        : '',
      status: defaults.status,
      status_: 'pending',
    };
  });
}
